import {
  Builder,
  CropRequest,
  EDoFRequest,
  EDoFRequestBuilder,
  QuantRequest,
  SiftRequest,
  SiftRequestBuilder,
  KidneyClassifierRequest,
  KidneyClassifierRequestBuilder,
  DeconvolutionRequest,
  DeconvolutionBuilder,
  YolosegdetectRequest,
  YolosegdetectRequestBuilder,
	ConvertRequest,
	ConvertRequestBuilder,
  RetinalLayerRequestBuilder,
  RetinalLayerRequest,
  StainAdapterRequest,
  StainAdapterRequestBuilder, StainAdapterNormalizeMethod,
  CellPoseRequest,
  CellPoseRequestBuilder,
  CellPoseSamRequest,
  CellPoseSamRequestBuilder,
  ImageJMacroRequest,
  ImageJMacroRequestBuilder,
  QupathScriptRequest,
  QupathScriptRequestBuilder,
  ScriptRequestBase,
  ScriptRequestBuilder,
  ScriptSourceMode,
  StorageKey,
} from './request';
import { IFileInfo } from './file-info';

export class RequestFactory {

  static getEdofRequestFromForm(formValue: any,
                                bucket: string | undefined,
                                parentPath: string | undefined): EDoFRequest {
    const edofBuilder = Object.assign(new EDoFRequestBuilder(), structuredClone(formValue));
    edofBuilder.bucket = bucket;
    edofBuilder.path = parentPath;
    return edofBuilder.build();
  }

  static getQuantRequestFromForm(formValue: any): QuantRequest {
    const newRequest = <QuantRequest>structuredClone(formValue);
    newRequest.fiberSettings.cMax = newRequest.fiberSettings.cMax / 100;
    newRequest.fiberSettings.cMin = newRequest.fiberSettings.cMin / 100;
    newRequest.nucleiSettings.cMax = newRequest.nucleiSettings.cMax / 100;
    newRequest.nucleiSettings.cMin = newRequest.nucleiSettings.cMin / 100;
    return Object.assign(new QuantRequest(), newRequest);
  }

  static getSegmentationRequestFromForm(formValue: any, bucket?: string, parentPath?: string): CropRequest {
    const newRequest = <CropRequest>structuredClone(formValue);
    newRequest.regionSettings.minimumAspectRatio = 0.15;
    newRequest.regionSettings.showRegions = false;
    newRequest.regionSettings.generateProofImage = true;
    newRequest.regionSettings.saveAsJson = true;
    newRequest.tileConfiguration.order = 'IMAGE';
    newRequest.tileConfiguration.clip = true;

    // outputFolder is a form-only string (see segmentation.component.ts) -
    // resolve it into the request's actual outputDir StorageKey and drop the
    // string so it isn't sent as a stray property.
    const outputFolder: string | undefined = (newRequest as any).outputFolder;
    delete (newRequest as any).outputFolder;
    if (outputFolder && !bucket) {
      // Caller didn't wire bucket/parentPath through, so the user-entered
      // outputFolder can't be resolved into outputDir - warn rather than
      // silently dropping it (jit-ui review on request.factory.ts).
      console.warn('getSegmentationRequestFromForm: outputFolder was entered but no bucket was provided; outputDir will not be set.');
    } else if (bucket && outputFolder) {
      const builder = new Builder();
      builder.bucket = bucket;
      builder.path = parentPath;
      newRequest.outputDir = outputFolder.startsWith('/')
        ? builder.keyForPath(outputFolder)
        : builder.keyForName(outputFolder);
    }

    return Object.assign(new CropRequest(), newRequest);
  }

  static getSiftRequestFromForm(formValue: any, bucket: string, parentPath: string): SiftRequest {
    const siftBuilder = Object.assign(new SiftRequestBuilder(), structuredClone(formValue));
    siftBuilder.bucket = bucket;
    siftBuilder.path = parentPath;
    return siftBuilder.build();
  }

  static getKidneyClassifierRequestFromForm(
    formValue: any,
    bucket: string,
    parentPath: string
  ): KidneyClassifierRequest {
    const kidneyBuilder = Object.assign(
      new KidneyClassifierRequestBuilder(),
      structuredClone(formValue)
    );
    kidneyBuilder.bucket = bucket;
    kidneyBuilder.path = parentPath;
    return kidneyBuilder.build();
  }

  static getDeconvolutionRequestFromForm(formValue: any, bucket: string | undefined,
                                         parentPath: string | undefined, inputFile: IFileInfo | undefined):
    DeconvolutionRequest {
    const deconvolutionBuilder = Object.assign(new DeconvolutionBuilder(), structuredClone(formValue));
    deconvolutionBuilder.bucket = bucket;
    deconvolutionBuilder.path = parentPath;
    deconvolutionBuilder.inputImage = inputFile;
    return deconvolutionBuilder.build();
  }

  static getStainAdapterRequestFromForm(formValue: any,
                                        bucket?: string,
                                        parentPath?: string,
                                        inputFiles?: IFileInfo[]): StainAdapterRequest {
    const stainAdapterBuilder = new StainAdapterRequestBuilder();
    stainAdapterBuilder.bucket = bucket;
    stainAdapterBuilder.path = parentPath;
    stainAdapterBuilder.inputImages = inputFiles;
    stainAdapterBuilder.normMethod = formValue.normMethod === StainAdapterNormalizeMethod.NONE ?
      undefined : formValue.normMethod;
    stainAdapterBuilder.patchSize = formValue.patchSize;
    stainAdapterBuilder.channelMode = formValue.channelMode;
    stainAdapterBuilder.ganModelPath = formValue.ganModelPath;
    stainAdapterBuilder.mode = formValue.mode;
    stainAdapterBuilder.normTargetPath = formValue.normTargetPath;
    stainAdapterBuilder.outputFolder = formValue.outputFolder;
    stainAdapterBuilder.saveNormalization = formValue.saveNormalization;
    return stainAdapterBuilder.build();
  }

  static  getYolosegdetectRequest(formValue: any, bucket: string | undefined,
                                         parentPath: string | undefined,
                                         inputFiles: IFileInfo[]): YolosegdetectRequest {
    const yolosegdetectBuilder = new YolosegdetectRequestBuilder();
    yolosegdetectBuilder.bucket = bucket;
    yolosegdetectBuilder.path = parentPath;
    yolosegdetectBuilder.inputImages = inputFiles;
    yolosegdetectBuilder.weightsPath = formValue.modelFile;
    yolosegdetectBuilder.outputFolder = formValue.outputFolder;
    yolosegdetectBuilder.level = formValue.level;
    yolosegdetectBuilder.downSamplingFactor = formValue.downSamplingFactor;
    yolosegdetectBuilder.overlapX = formValue.overlapX;
    yolosegdetectBuilder.overlapY = formValue.overlapY;
    yolosegdetectBuilder.confidence = formValue.confidence;
    yolosegdetectBuilder.iouThreshold = formValue.iouThreshold;
    yolosegdetectBuilder.nmsThreshold = formValue.nmsThreshold;
		yolosegdetectBuilder.saveSegment = formValue.saveSegment;
		yolosegdetectBuilder.db_upsert = formValue.db_upsert;
    return yolosegdetectBuilder.build();
  }

  static getCellPoseRequest(formValue: any, bucket: string | undefined,
                            parentPath: string | undefined,
                            inputFiles: IFileInfo[]): CellPoseRequest {
    const builder = new CellPoseRequestBuilder();
    builder.bucket = bucket;
    builder.path = parentPath;
    builder.inputImages = inputFiles;
    builder.model             = formValue.model;
    builder.modelLocationPath = formValue.modelLocationPath;
    builder.outputFolder      = formValue.outputFolder;
    builder.diameter          = formValue.diameter;
    builder.channelMode       = formValue.channelMode;
    builder.cytoChannel       = formValue.cytoChannel;
    builder.nucChannel        = formValue.nucChannel;
    builder.flowThreshold     = formValue.flowThreshold;
    builder.cellprobThreshold = formValue.cellprobThreshold;
    builder.minSize           = formValue.minSize;
    builder.do3D              = formValue.do3D;
    builder.normalize         = formValue.normalize;
    builder.invert            = formValue.invert;
    builder.augment           = formValue.augment;
    builder.resample          = formValue.resample;
    builder.tileOverlap       = formValue.tileOverlap;
    builder.batchSize         = formValue.batchSize;
    builder.stitchThreshold   = formValue.stitchThreshold;
    builder.saveMaskPng       = formValue.saveMaskPng;
    return builder.build();
  }

  static getCellPoseSamRequest(formValue: any, bucket: string | undefined,
                               parentPath: string | undefined,
                               inputFiles: IFileInfo[]): CellPoseSamRequest {
    const builder = new CellPoseSamRequestBuilder();
    builder.bucket = bucket;
    builder.path = parentPath;
    builder.inputImages = inputFiles;
    builder.model             = formValue.model;
    builder.modelLocationPath = formValue.modelLocationPath;
    builder.outputFolder      = formValue.outputFolder;
    builder.diameter          = formValue.diameter;
    builder.channelMode       = formValue.channelMode;
    builder.cytoChannel       = formValue.cytoChannel;
    builder.nucChannel        = formValue.nucChannel;
    builder.cellprobThreshold = formValue.cellprobThreshold;
    builder.saveMaskPng       = formValue.saveMaskPng;
    return builder.build();
  }

  /**
   * @param formValue the composer form's value (outputFolder, saveAsReusable, scriptName, scriptDescription)
   * @param sourceMode which of savedScriptId/scriptKey/scriptBody to submit
   * @param savedScriptId set when sourceMode === 'saved'
   * @param scriptBody set when sourceMode === 'scriptBody' (covers both the "write inline" and
   *        "upload from my computer" source modes - both just populate this same field)
   * @param scriptKey set when sourceMode === 'scriptKey' (the storage picker's selection)
   * @param parameters the parameters table's rows, reduced to a plain string map
   * @param annotations the viewer's current regions as a parsed GeoJSON FeatureCollection, or
   *        undefined if the user hasn't drawn/selected any - see IVisualizer.getGeoJsonString
   */
  static getQupathScriptRequest(
    formValue: any, bucket: string | undefined, parentPath: string | undefined,
    sourceMode: ScriptSourceMode, savedScriptId: number | undefined,
    scriptBody: string | undefined, scriptKey: StorageKey | undefined,
    parameters: { [key: string]: string }, annotations: unknown,
  ): QupathScriptRequest {
    return RequestFactory.fillScriptRequest(new QupathScriptRequestBuilder(), formValue, bucket, parentPath,
      sourceMode, savedScriptId, scriptBody, scriptKey, parameters, annotations);
  }

  /**
   * Same arguments and behaviour as {@link getQupathScriptRequest}, for an ImageJ1 macro - the
   * request differs only in its process type, which routes it to the ImageJ macro worker.
   */
  static getImageJMacroRequest(
    formValue: any, bucket: string | undefined, parentPath: string | undefined,
    sourceMode: ScriptSourceMode, savedScriptId: number | undefined,
    scriptBody: string | undefined, scriptKey: StorageKey | undefined,
    parameters: { [key: string]: string }, annotations: unknown,
  ): ImageJMacroRequest {
    return RequestFactory.fillScriptRequest(new ImageJMacroRequestBuilder(), formValue, bucket, parentPath,
      sourceMode, savedScriptId, scriptBody, scriptKey, parameters, annotations);
  }

  private static fillScriptRequest<T extends ScriptRequestBase>(
    builder: ScriptRequestBuilder<T>,
    formValue: any, bucket: string | undefined, parentPath: string | undefined,
    sourceMode: ScriptSourceMode, savedScriptId: number | undefined,
    scriptBody: string | undefined, scriptKey: StorageKey | undefined,
    parameters: { [key: string]: string }, annotations: unknown,
  ): T {
    builder.bucket = bucket;
    builder.path = parentPath;
    builder.sourceMode = sourceMode;
    builder.savedScriptId = savedScriptId;
    builder.scriptBody = scriptBody;
    builder.scriptKey = scriptKey;
    builder.outputFolder = formValue.outputFolder;
    builder.parameters = parameters;
    builder.annotations = annotations;
    builder.saveAsReusable = formValue.saveAsReusable;
    builder.scriptName = formValue.scriptName;
    builder.scriptDescription = formValue.scriptDescription;
    return builder.build();
  }

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static getConvertRequest(formValue: any, bucket: string | undefined,
	                                       parentPath: string | undefined): ConvertRequest {

	  const convertBuilder = new ConvertRequestBuilder();
		convertBuilder.bucket = bucket;
		convertBuilder.path = parentPath;
		convertBuilder.outFile = formValue.outputName;
		convertBuilder.outputFolder = formValue.outputFolder;
		convertBuilder.series = formValue.level;
		convertBuilder.compression = formValue.compression;
		convertBuilder.channel = formValue.channel;
		convertBuilder.z = formValue.z;
		convertBuilder.timepoint = formValue.timepoint;
		convertBuilder.tilex = formValue.tilex;
		convertBuilder.tiley = formValue.tiley;
		return convertBuilder.build();
	}
																					
  static getRetinalLayerRequest(bucket: string | undefined,
                                          parentPath: string | undefined,
                                          inputFiles: IFileInfo[],
                                          modelPath: string,
                                          model: string,
                                          step: number,
                                          patchSize: number,
                                          outputFolder: string):RetinalLayerRequest {
    const retinalLayerBuilder = new RetinalLayerRequestBuilder();
    retinalLayerBuilder.bucket = bucket;
    retinalLayerBuilder.path = parentPath;
    retinalLayerBuilder.images = inputFiles;
    retinalLayerBuilder.predModel = modelPath;
    retinalLayerBuilder.model = model;
    retinalLayerBuilder.step = step;
    retinalLayerBuilder.patchSize = patchSize;
    retinalLayerBuilder.outputPath = outputFolder;
    return retinalLayerBuilder.build();
  }
}
